import axios from 'axios';
import { config } from '../config/environment';

const api = axios.create({
  baseURL: config.api.baseUrl,
});

export const SESSAO_EXPIRADA_KEY = 'sessaoExpiradaMsg';
export const SESSAO_EXPIRADA_MSG = 'Sua sessão expirou ou é inválida. Refaça o login para continuar.';

// Registra o aviso exibido na tela de login após a expulsão por token inválido/vencido
const registrarSessaoExpirada = () => {
  sessionStorage.setItem(SESSAO_EXPIRADA_KEY, SESSAO_EXPIRADA_MSG);
};

// Interceptor para adicionar token automaticamente
api.interceptors.request.use(
  (config) => {
    // Verificar se é uma rota do garçom ou se o usuário está no módulo do garçom
    const isGarcomPath = typeof window !== 'undefined' && window.location.pathname.startsWith('/garcom');
    const isGarcomRoute = config.url?.includes('/garcom/') || isGarcomPath;
    
    // Verificar se é uma rota pública que não precisa de autenticação
    // Para categorias, apenas GET é público (leitura), POST/PUT/DELETE precisam de autenticação
    const isPublicRoute = config.url?.includes('/api/menu/') || 
                         (config.url?.includes('/api/categorias/') && config.method?.toLowerCase() === 'get') ||
                         (config.url?.includes('/api/empresa/') && config.url?.includes('/public')) ||
                         config.url?.includes('/api/meta-pixel/') ||
                         config.url?.includes('/api/horario-funcionamento/status/') ||
                         config.url?.includes('/api/chamar-garcom/') ||
                         config.url?.includes('/api/menu/produto/');
    
    // Verificar se é uma rota de login/autenticação
    const isAuthRoute = config.url?.includes('/api/auth/login') || 
                       config.url?.includes('/api/auth/registro') ||
                       config.url?.includes('/api/admin/login');
    
    // Só adicionar token se não for rota pública ou de autenticação
    if (!isPublicRoute && !isAuthRoute) {
      if (isGarcomRoute) {
        // Para rotas do garçom, usar o token do garçom
        let garcomToken = localStorage.getItem('garcomToken');
        let garcomTokenExpiration = localStorage.getItem('garcomTokenExpiration');
        
        if (!garcomToken || !garcomTokenExpiration) {
          garcomToken = sessionStorage.getItem('garcomToken');
          garcomTokenExpiration = sessionStorage.getItem('garcomTokenExpiration');
        }
        
        // Verificar se o token expirou
        if (garcomToken && garcomTokenExpiration) {
          const expirationDate = new Date(garcomTokenExpiration);
          const agora = new Date();
          
          if (expirationDate <= agora) {
            localStorage.removeItem('garcomToken');
            localStorage.removeItem('garcomUsuario');
            localStorage.removeItem('garcomTokenExpiration');
            sessionStorage.removeItem('garcomToken');
            sessionStorage.removeItem('garcomUsuario');
            sessionStorage.removeItem('garcomTokenExpiration');
            registrarSessaoExpirada();
            
            const currentPath = window.location.pathname;
            const isOnLoginPage = currentPath.includes('/garcom/login');
            if (!isOnLoginPage) {
              window.location.href = '/garcom/login';
            }
            return Promise.reject(new Error('Token do garçom expirado'));
          }
        }
        
        if (garcomToken) {
          config.headers.Authorization = `Bearer ${garcomToken}`;
        }
      } else {
        // Para outras rotas, usar o token do usuário comum
        let token = localStorage.getItem('authToken');
        let tokenExpiration = localStorage.getItem('authTokenExpiration');
        
        if (!token || !tokenExpiration) {
          token = sessionStorage.getItem('authToken');
          tokenExpiration = sessionStorage.getItem('authTokenExpiration');
        }
        
        // Verificar se o token expirou
        if (token && tokenExpiration) {
          const expirationDate = new Date(tokenExpiration);
          const agora = new Date();
          
          if (expirationDate <= agora) {
            localStorage.removeItem('authToken');
            localStorage.removeItem('authUsuario');
            localStorage.removeItem('authTokenExpiration');
            sessionStorage.removeItem('authToken');
            sessionStorage.removeItem('authUsuario');
            sessionStorage.removeItem('authTokenExpiration');
            registrarSessaoExpirada();
            
            const currentPath = window.location.pathname;
            const isOnLoginPage = currentPath.includes('/login') || currentPath.includes('/admin/login');
            if (!isOnLoginPage) {
              const empresaId = currentPath.split('/')[2];
              if (empresaId) {
                window.location.href = `/login/${empresaId}`;
              } else {
                window.location.href = '/login';
              }
            }
            return Promise.reject(new Error('Token expirado'));
          }
        }
        
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para lidar com erros de autenticação
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      // Verificar se é uma rota do garçom ou se estamos no módulo do garçom
      const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
      const isGarcomPath = currentPath.startsWith('/garcom');
      const isGarcomRoute = error.config?.url?.includes('/garcom/') || isGarcomPath;
      
      // Verificar se é uma rota pública que não precisa de autenticação
      // Para categorias, apenas GET é público (leitura), POST/PUT/DELETE precisam de autenticação
      const isPublicRoute = error.config?.url?.includes('/api/menu/') || 
                           (error.config?.url?.includes('/api/categorias/') && error.config?.method?.toLowerCase() === 'get') ||
                           (error.config?.url?.includes('/api/empresa/') && error.config?.url?.includes('/public')) ||
                           error.config?.url?.includes('/api/meta-pixel/') ||
                           error.config?.url?.includes('/api/horario-funcionamento/status/') ||
                           error.config?.url?.includes('/api/chamar-garcom/') ||
                           error.config?.url?.includes('/api/menu/produto/');
      
      // Verificar se é uma rota de login/autenticação
      const isAuthRoute = error.config?.url?.includes('/api/auth/login') || 
                         error.config?.url?.includes('/api/auth/registro') ||
                         error.config?.url?.includes('/api/admin/login');
      
      // Verificar se é a rota de pedidos do usuário (deixar o componente tratar o erro)
      const isPedidosUsuarioRoute = error.config?.url?.includes('/api/pedidos/usuario/');
      
      // Verificar se estamos em uma página pública (menu, inicial, etc.)
      const isOnPublicPage = currentPath === '/' || 
                             currentPath.startsWith('/menu/') ||
                             currentPath.includes('/login') || 
                             currentPath.includes('/admin/login') ||
                             currentPath.includes('/registro') ||
                             currentPath.includes('/recuperar-senha');
      
      if (isPublicRoute || isAuthRoute || isPedidosUsuarioRoute || isOnPublicPage) {
        // Para rotas públicas, de autenticação, pedidos do usuário e páginas públicas,
        // apenas retornar o erro sem redirecionar
        return Promise.reject(error);
      }
      
      if (isGarcomRoute) {
        // Tolerância zero: limpar dados do garçom e redirecionar para login do garçom
        localStorage.removeItem('garcomToken');
        localStorage.removeItem('garcomUsuario');
        localStorage.removeItem('garcomTokenExpiration');
        sessionStorage.removeItem('garcomToken');
        sessionStorage.removeItem('garcomUsuario');
        sessionStorage.removeItem('garcomTokenExpiration');
        registrarSessaoExpirada();
        window.location.href = '/garcom/login';
      } else {
        // Tolerância zero: qualquer 401/403 em rota protegida encerra a sessão
        console.log('⚠️ [API-INTERCEPTOR] Token inválido ou expirado. Limpando dados de autenticação.');
        localStorage.removeItem('authToken');
        localStorage.removeItem('authUsuario');
        localStorage.removeItem('authTokenExpiration');
        sessionStorage.removeItem('authToken');
        sessionStorage.removeItem('authUsuario');
        sessionStorage.removeItem('authTokenExpiration');

        const isOnLoginPage = currentPath.includes('/login') || currentPath.includes('/admin/login');

        if (!isOnLoginPage) {
          registrarSessaoExpirada();
          // Redirecionar para login com a empresa atual
          const empresaId = currentPath.split('/')[2]; // /menu/:empresaId
          if (empresaId) {
            window.location.href = `/login/${empresaId}`;
          } else {
            window.location.href = '/login';
          }
        }
      }
    }
    return Promise.reject(error);
  }
);

export default api;
